import React, { FC } from 'react';
import { Text } from '@wix/design-system';
import { Check } from '@wix/wix-ui-icons-common';
import { useAtomValue } from 'jotai';
import { isThemeLoadingAtom } from '../../atoms';
import { useSelectedThemeColor, useSetSelectedThemeColor } from '../../../../store/appStore';
import cs from 'classnames';
import s from './TemplateGrid.scss';
import { THEME_KEYS_TRANSLATIONS } from '@common/constants/themes';
import { templateColors } from '../../types';

interface TemplateGridProps {
  isDisabled?: boolean;
}

const getGradientBackground = (colors: string[]) => {
  return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
};

export const TemplateGrid: FC<TemplateGridProps> = ({ isDisabled = false }) => {
  const selectedThemeColor = useSelectedThemeColor();
  const setSelectedThemeColor = useSetSelectedThemeColor();
  const isLoading = useAtomValue(isThemeLoadingAtom);
  const isInteractionDisabled = isDisabled || isLoading;

  return (
    <div className={s.templateGrid}>
      {templateColors.map((template) => {
        const isSelected = selectedThemeColor === template.name;
        return (
          <div
            key={template.name}
            className={cs(s.templateCard, {
              [s.selected]: isSelected,
              [s.disabled]: isInteractionDisabled,
            })}
            onClick={() => !isInteractionDisabled && setSelectedThemeColor(template.name)}
            role="button"
            tabIndex={isInteractionDisabled ? -1 : 0}
            onKeyDown={(e) => {
              if (!isInteractionDisabled && (e.key === 'Enter' || e.key === ' ')) {
                setSelectedThemeColor(template.name);
              }
            }}
          >
            <div className={s.cardPreview} style={{ background: getGradientBackground(template.colors) }}>
              {isSelected && (
                <div className={s.checkmark}>
                  <Check size="20px" />
                </div>
              )}

              <div className={s.colorDots}>
                {template.colors.map((color, index) => (
                  <div key={index} className={s.colorDot} style={{ '--color': color } as React.CSSProperties} />
                ))}
              </div>
            </div>

            <div className={s.cardContent}>
              <Text weight="bold" className={s.templateName}>
                {THEME_KEYS_TRANSLATIONS[template.name]}
              </Text>
              <Text secondary className={s.templateDescription}>
                {template.description}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
};
